import { dedupeWords, normalizeWord, pickPrompt, validateWord } from './data';
import { timing, variantForLevel, } from './params';
export { validateWord };
export function makeStimulus(trialIdx, value, level, lang) {
    const variant = variantForLevel(level);
    const clampedDur = Math.max(30_000, Math.min(90_000, Math.round(value)));
    const prompt = pickPrompt(variant.mode, lang, trialIdx);
    return {
        trialIdx,
        value: clampedDur,
        prompt,
    };
}
export function createVerbalFluencyAdapter(level = 1, lang = 'fr') {
    let currentLang = lang;
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        words: [],
        remainingMs: 0,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    let tickInterval = null;
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(stim) {
        if (!resolver)
            return;
        const wordCount = state.words.length;
        const r = resolver;
        resolver = null;
        rejecter = null;
        clearTimers();
        r({
            words: [...state.words],
            wordCount,
            correct: wordCount >= state.variant.targetScore,
            reactionTimeMs: Date.now() - trialStart,
        });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'priming',
                    stim,
                    feedback: null,
                    words: [],
                    remainingMs: stim.value,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'awaiting_input' });
                    trialStart = Date.now();
                    tickInterval = setInterval(() => {
                        const elapsed = Date.now() - trialStart;
                        const remaining = Math.max(0, stim.value - elapsed);
                        if (remaining <= 0) {
                            if (tickInterval !== null) {
                                clearInterval(tickInterval);
                                tickInterval = null;
                            }
                            if (state.subPhase === 'awaiting_input') {
                                update({ remainingMs: 0 });
                                settle(stim);
                            }
                        }
                        else {
                            update({ remainingMs: remaining });
                        }
                    }, 250);
                    timers.push(setTimeout(() => {
                        if (state.subPhase === 'awaiting_input') {
                            settle(stim);
                        }
                    }, stim.value + 100));
                }, timing.primingMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // M2 alpha : no-op.
        },
        resume() {
            // M2 alpha : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        addWord(input) {
            if (state.subPhase !== 'awaiting_input' || state.stim === null) {
                return { accepted: false, reason: 'phase' };
            }
            const prompt = state.stim.prompt;
            if (!validateWord(input, prompt, currentLang)) {
                const norm = normalizeWord(input);
                if (norm.length < 2)
                    return { accepted: false, reason: 'too-short' };
                if (prompt.mode === 'phonemic' && norm[0]?.toLowerCase() !== prompt.key.toLowerCase()) {
                    return { accepted: false, reason: 'wrong-letter' };
                }
                return { accepted: false, reason: 'invalid' };
            }
            const next = dedupeWords([...state.words, input]);
            if (next.length === state.words.length) {
                return { accepted: false, reason: 'duplicate' };
            }
            update({ words: next });
            return { accepted: true };
        },
        finishRound() {
            if (state.subPhase !== 'awaiting_input' || state.stim === null)
                return;
            settle(state.stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
        setLang(nextLang) {
            currentLang = nextLang;
        },
    };
}
//# sourceMappingURL=adapter.js.map